"use client";

import type { ReactNode } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  ContactEmailForm,
  type ContactEmailFormContext,
} from "./contact-email-form";

/**
 * The email ask, as a drawer.
 *
 * Used on the wallet ticket page and in settings. Checkout keeps its ordinary
 * email ask inline and code-free; it opens this drawer only when a member
 * explicitly tries to replace an address that is already verified.
 */

const CONTACT_EMAIL_LAYER = 10000;

export type ContactEmailDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: ContactEmailFormContext;
  /** Booking whose ticket is mailed once the address is confirmed. */
  eventBookingId?: number | null;
  initialEmail?: string | null;
  /** Opens straight on the code step when a code is already out. */
  pendingEmail?: string | null;
  onVerified?: (email: string) => void;
  /**
   * What to do when the person declines. Defaults to just closing the drawer.
   *
   * The tier-join surface passes a handler that CONTINUES with the request:
   * skipping the email must not also silently abandon the thing they came to
   * do — that would punish the decline instead of honouring it.
   */
  onSkip?: () => void;
  skipLabel?: string;
  skipNote?: ReactNode;
  title?: string;
  description?: string;
};

export function ContactEmailDrawer({
  open,
  onOpenChange,
  context,
  eventBookingId,
  initialEmail,
  pendingEmail,
  onVerified,
  onSkip,
  skipLabel,
  skipNote,
  title = "Get your ticket by email",
  description = "Add an email and we'll send a copy of this ticket to your inbox.",
}: ContactEmailDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        layer={CONTACT_EMAIL_LAYER}
        className="mx-auto w-full max-w-md rounded-t-[28px] border-border bg-surface text-text-primary dark:border-white/[0.12] dark:bg-[#111114] dark:text-white"
      >
        <DrawerHeader className="px-4 pb-2 text-left">
          <DrawerTitle className="text-xl font-semibold tracking-tight">
            {title}
          </DrawerTitle>
          <DrawerDescription className="mt-1 text-sm">
            {description}
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-4 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
          {open ? (
            <ContactEmailForm
              context={context}
              eventBookingId={eventBookingId ?? null}
              initialEmail={initialEmail ?? null}
              pendingEmail={pendingEmail ?? null}
              onVerified={(email) => {
                onOpenChange(false);
                onVerified?.(email);
              }}
              skipLabel={skipLabel}
              skipNote={skipNote}
              onSkip={() => {
                onOpenChange(false);
                onSkip?.();
              }}
            />
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
